import { useCallback } from "react";
import { adminPost } from "@/lib/kbAdminClient";
import type { PanelNote, SearchResult } from "./types";

type ShowNote = (note: PanelNote | null) => void;

type Params = {
  q: string;
  searchAudience: string;
  searchScope: string;
  searchDocType: string;
  searchStatus: string;
  quickAuditRisk: "all" | "low" | "moderate" | "healthy";
  canUseQuickChunkAudit: boolean;
  canPublishKb: boolean;
  setDraftCount: (v: number) => void;
  setPendingProposalCount: (v: number) => void;
  setDraftsLoading: (v: boolean) => void;
  setResults: (v: SearchResult[]) => void;
  setSearchStatus: (v: any) => void;
  showNote: ShowNote;
};

export function useKbLibraryWorkflow(p: Params) {
  const refreshDraftCount = useCallback(async () => {
    const response = await adminPost<{ ok: true; count: number }>("/api/kb/admin/drafts", { includeList: false });
    if (response.ok) p.setDraftCount(Number(response.data.count || 0));
  }, [p.setDraftCount]);

  const refreshPendingProposalCount = useCallback(async () => {
    if (!p.canPublishKb) return;
    const response = await adminPost<{ ok: true; proposals: unknown[] }>("/api/kb/admin/listProposals", { status: "pending", limit: 100 });
    if (!response.ok && response.status === 503) {
      // Some orgs/environments intentionally run without proposals table. Keep UI quiet and continue.
      p.setPendingProposalCount(0);
      return;
    }
    if (response.ok) p.setPendingProposalCount((response.data as { proposals?: unknown[] }).proposals?.length ?? 0);
  }, [p.canPublishKb, p.setPendingProposalCount]);

  const loadDraftsIntoResults = useCallback(async () => {
    p.showNote(null);
    p.setDraftsLoading(true);
    try {
      const response = await adminPost<{ ok: true; count: number; drafts: any[] }>("/api/kb/admin/drafts", { includeList: true, limit: 50 });
      if (!response.ok) {
        p.setResults([]);
        p.showNote({ tone: "error", title: "Draft load failed", text: response.error });
        return;
      }
      p.setDraftCount(Number(response.data.count || 0));
      p.setResults((response.data.drafts || []).map((draft: any) => ({
        id: draft.id, slug: draft.slug, title: draft.title, audience: draft.audience ?? "internal", scope: draft.scope ?? null,
        doc_type: draft.doc_type ?? null, locations: draft.locations ?? [], tags: draft.tags ?? [], status: draft.status ?? "draft",
        published_at: null, last_edited_by: draft.last_edited_by ?? null,
      })));
      p.setSearchStatus("draft");
      p.showNote({ tone: "info", title: "Drafts loaded", text: `Loaded ${response.data.drafts?.length ?? 0} draft articles into the library list.` });
    } finally {
      p.setDraftsLoading(false);
    }
  }, [p.setDraftCount, p.setDraftsLoading, p.setResults, p.setSearchStatus, p.showNote]);

  const doSearch = useCallback(async () => {
    p.showNote(null);
    const response = await adminPost<{ ok: true; results: SearchResult[] }>("/api/kb/admin/search", {
      q: p.q, audience: p.searchAudience || undefined, scope: p.searchScope || undefined, docType: p.searchDocType || undefined, status: p.searchStatus || undefined, limit: 25,
    });
    if (!response.ok) {
      p.setResults([]);
      p.showNote({ tone: "error", title: "Search failed", text: response.error });
      return;
    }
    p.setResults(response.data.results || []);
    if ((response.data.results || []).length === 0) p.showNote({ tone: "info", title: "No matches", text: "No Knowledge Base articles matched the current filters." });
  }, [p.q, p.searchAudience, p.searchDocType, p.searchScope, p.searchStatus, p.setResults, p.showNote]);

  const doChunkAudit = useCallback(async () => {
    if (!p.canUseQuickChunkAudit) return;
    p.showNote(null);
    const response = await adminPost<{ ok: true; results: SearchResult[]; count: number }>("/api/kb/admin/chunkAudit", { q: p.q, risk: p.quickAuditRisk, limit: 250 });
    if (!response.ok) {
      p.showNote({ tone: "error", title: "Chunk audit failed", text: response.error });
      return;
    }
    p.setResults(response.data.results || []);
    p.setSearchStatus("");
    p.showNote({ tone: "info", title: "Chunk audit ready", text: `Loaded ${response.data.count ?? response.data.results?.length ?? 0} article(s) for ${p.quickAuditRisk} chunk review.` });
  }, [p.canUseQuickChunkAudit, p.q, p.quickAuditRisk, p.setResults, p.setSearchStatus, p.showNote]);

  return { refreshDraftCount, refreshPendingProposalCount, loadDraftsIntoResults, doSearch, doChunkAudit };
}
